import type { BuildingType } from '@aztec-realms/shared-types';

import { registeredArtworkScale } from './settlement-building-art';
import {
  applyIslandHomography,
  createSettlementIslandRegistration,
  islandLateralScale,
  type IslandCoverRectangle,
  type IslandQuad,
  type SettlementIslandRegistration,
} from './settlement-island-registration';

export interface SettlementGroundProjection {
  x: number;
  y: number;
  depthScale: number;
  groundScale: number;
}

export interface SettlementProjectedFootprint {
  /** Lateral extent midpoint at front ground depth; not necessarily a polygon corner. */
  anchor: SettlementGroundProjection;
  width: number;
  depth: number;
}

/** Derive contact and size from the same projected plot, never from an unrelated sprite multiplier. */
export function projectSettlementFootprint(
  bounds: SettlementGridBounds,
  viewportWidth: number,
  viewportHeight: number,
  tileWidth: number,
  tileHeight: number,
  frame?: SettlementGridFrame,
): SettlementProjectedFootprint {
  const corners = [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.maxX, bounds.maxY],
    [bounds.minX, bounds.maxY],
  ].map(([x, y]) =>
    projectSettlementGround(x!, y!, viewportWidth, viewportHeight, tileWidth, tileHeight, frame),
  );
  const left = Math.min(...corners.map((point) => point.x));
  const right = Math.max(...corners.map((point) => point.x));
  const back = Math.min(...corners.map((point) => point.y));
  const front = corners[2]!;
  return {
    anchor: { ...front, x: (left + right) / 2 },
    width: right - left,
    depth: front.y - back,
  };
}

/** Match the pinhole ground derivative to the authored 2:1 plane at reference depth. */
export function settlementTileWidth(viewportHeight: number): number {
  return (
    (viewportHeight * SETTLEMENT_CAMERA_PROFILE.groundFocalRatio * 4) /
    (SETTLEMENT_CAMERA_PROFILE.cameraGridDepth - SETTLEMENT_CAMERA_PROFILE.referenceGridDepth)
  );
}

export interface SettlementGridBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** API footprints are effective, already rotated extents; authored sizes are fixture fallback only. */
export function settlementBuildingBounds(
  building: {
    gridX: number;
    gridY: number;
    orientation: number;
    footprintWidth?: number;
    footprintHeight?: number;
  },
  authoredFootprint: readonly [number, number],
): SettlementGridBounds {
  const rotated = building.orientation % 2 !== 0;
  return {
    minX: building.gridX,
    maxX: building.gridX + (building.footprintWidth ?? authoredFootprint[rotated ? 1 : 0]),
    minY: building.gridY,
    maxY: building.gridY + (building.footprintHeight ?? authoredFootprint[rotated ? 0 : 1]),
  };
}

/**
 * Uniform alpha-padding correction, independent of logical footprint extent.
 * Larger API plots must produce larger artwork. Never divide by occupied dimensions.
 * Apply only to upright artwork, never to contact, selection, camera fit or drag.
 */
export function settlementBuildingArtScale(
  building: {
    type?: BuildingType;
    level?: number;
    orientation?: number;
    footprintWidth?: number;
    footprintHeight?: number;
  },
  authoredFootprint: readonly [number, number],
): number {
  void authoredFootprint; // Kept for existing fixture callers; only bounds may use this fallback.
  return building.type
    ? registeredArtworkScale(building.type, building.level, 'completed', building.orientation)
    : 1;
}

/** World graphics are magnified by the outer camera; guides retain their CSS-pixel weight. */
export function settlementGridGuideStroke(zoom: number) {
  const scale = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return { recess: 1.8 / scale, seam: 0.75 / scale, boundary: 1.5 / scale };
}

export interface SettlementGridFrame {
  centerX: number;
  centerY: number;
  scale: number;
  view?: { zoom: number; offsetX: number; offsetY: number };
  /** Registered gameplay is composed with the original cover image, never a grid-only camera. */
  registration?: SettlementIslandRegistration;
}

/** Calibrate one explicit district while preserving the existing pinhole basis/inverse. */
export function registerSettlementIslandFrame(
  frame: SettlementGridFrame,
  district: SettlementGridBounds,
  width: number,
  height: number,
  districtCode = 'lakeside_outpost',
): SettlementGridFrame | undefined {
  const basis = { ...frame, view: undefined, registration: undefined };
  const tile = settlementTileWidth(height);
  const source = [
    [district.minX, district.minY],
    [district.maxX, district.minY],
    [district.maxX, district.maxY],
    [district.minX, district.maxY],
  ].map(([x, y]) =>
    projectSettlementGround(x!, y!, width, height, tile, tile / 2, basis),
  ) as unknown as IslandQuad;
  const registration = createSettlementIslandRegistration(source, width, height, districtCode);
  return registration ? { ...basis, registration } : undefined;
}

/** Bounds are exclusive geometry corners, not the API's inclusive last-tile coordinates. */
export function createSettlementDistrictFrame(
  district: { code: string; bounds: SettlementGridBounds },
  width: number,
  height: number,
): SettlementGridFrame | undefined {
  const basis = fitSettlementGrid([district.bounds]);
  return basis
    ? registerSettlementIslandFrame(basis, district.bounds, width, height, district.code)
    : undefined;
}

export interface SettlementCameraView {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

/** Manual inspection may zoom closer; automatic fit must not magnify the backdrop excessively. */
export const SETTLEMENT_REGISTERED_EDITOR_MAX_ZOOM = 16;
export const SETTLEMENT_EDITOR_FIT_MAX_ZOOM = 4;

export function initialSettlementCamera(width: number, height: number): SettlementCameraView {
  // The city is explored by panning; do not miniaturize every district into one phone frame.
  const zoom = height < 420 ? 1.65 : width < 700 ? 2.1 : 1.65;
  const focus = { x: width * 0.5, y: height * 0.55 };
  return clampSettlementCamera(
    {
      zoom,
      offsetX: width * 0.54 - focus.x * zoom,
      offsetY: height * 0.56 - focus.y * zoom,
    },
    width,
    height,
  );
}

/** Keep the actual image covering the viewport; off-screen image margins remain pannable. */
export function clampSettlementCamera(
  view: SettlementCameraView,
  width: number,
  height: number,
  maxZoom = 3,
  cover?: IslandCoverRectangle,
): SettlementCameraView {
  const zoom = Math.min(maxZoom, Math.max(1, view.zoom));
  const image =
    cover &&
    [cover.x, cover.y, cover.width, cover.height].every(Number.isFinite) &&
    cover.x <= 0 &&
    cover.y <= 0 &&
    cover.x + cover.width >= width - 1e-8 &&
    cover.y + cover.height >= height - 1e-8
      ? cover
      : { x: 0, y: 0, width, height };
  return {
    zoom,
    offsetX: Math.min(
      0 - image.x * zoom,
      Math.max(width - (image.x + image.width) * zoom, view.offsetX),
    ),
    offsetY: Math.min(
      0 - image.y * zoom,
      Math.max(height - (image.y + image.height) * zoom, view.offsetY),
    ),
  };
}

/** Zoom around the fingers/cursor, preserving the world point beneath them. */
export function zoomSettlementCamera(
  view: SettlementCameraView,
  zoom: number,
  anchor: { x: number; y: number },
  width: number,
  height: number,
  maxZoom = 3,
  cover?: IslandCoverRectangle,
): SettlementCameraView {
  const nextZoom = Math.min(maxZoom, Math.max(1, zoom));
  const ratio = nextZoom / view.zoom;
  return clampSettlementCamera(
    {
      zoom: nextZoom,
      offsetX: anchor.x - (anchor.x - view.offsetX) * ratio,
      offsetY: anchor.y - (anchor.y - view.offsetY) * ratio,
    },
    width,
    height,
    maxZoom,
    cover,
  );
}

/** Globally compose the occupied city; terrain props use this same camera transform. */
export function focusSettlementGrid(
  frame: SettlementGridFrame,
  occupied: readonly SettlementGridBounds[],
  width: number,
  height: number,
  tileWidth: number,
  tileHeight: number,
): SettlementGridFrame {
  if (occupied.length === 0) return frame;
  const points = occupied.flatMap((bounds) =>
    [
      [bounds.minX, bounds.minY],
      [bounds.minX, bounds.maxY],
      [bounds.maxX, bounds.minY],
      [bounds.maxX, bounds.maxY],
    ].map(([x, y]) => projectSettlementGround(x!, y!, width, height, tileWidth, tileHeight, frame)),
  );
  const padding = tileWidth * Math.max(...points.map((point) => point.depthScale));
  const minX = Math.min(...points.map((point) => point.x)) - padding;
  const maxX = Math.max(...points.map((point) => point.x)) + padding;
  const minY = Math.min(...points.map((point) => point.y)) - padding * 1.5;
  const maxY = Math.max(...points.map((point) => point.y)) + padding * 0.4;
  const zoom = Math.max(
    1,
    Math.min(3.6, (width * 0.84) / (maxX - minX), (height * 0.52) / (maxY - minY)),
  );
  return {
    ...frame,
    view: {
      zoom,
      offsetX: width * 0.5 - (minX + maxX) * 0.5 * zoom,
      offsetY: height * 0.5 - (minY + maxY) * 0.5 * zoom,
    },
  };
}

/** One camera-space transform for the complete live grid, including footprint corners. */
export function fitSettlementGrid(
  bounds: readonly SettlementGridBounds[],
): SettlementGridFrame | undefined {
  if (bounds.length === 0) return undefined;
  const minX = Math.min(...bounds.map((item) => item.minX));
  const maxX = Math.max(...bounds.map((item) => item.maxX));
  const minY = Math.min(...bounds.map((item) => item.minY));
  const maxY = Math.max(...bounds.map((item) => item.maxY));
  if (![minX, maxX, minY, maxY].every(Number.isFinite)) return undefined;
  return {
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    // Fit each axis inside [1,7], hence every footprint depth inside [2,14].
    scale: Math.min(1, 6 / Math.max(1, maxX - minX, maxY - minY)),
  };
}

/**
 * Compose inhabited geography, not every empty unlocked expansion. Keep the first
 * unlocked district as a permanent anchor even after its last building moves.
 * District metadata uses inclusive tile bounds; returned/placement bounds are corners.
 */
export function settlementFramingDistricts(
  districts: readonly {
    code: string;
    unlockOrder: number;
    unlocked: boolean;
    bounds: SettlementGridBounds;
  }[],
  inhabitedDistrictCodes: readonly string[],
  placementTarget?: SettlementGridBounds,
): SettlementGridBounds[] {
  const unlocked = districts.filter((district) => district.unlocked);
  const primary = unlocked.reduce<(typeof unlocked)[number] | undefined>(
    (first, district) => (!first || district.unlockOrder < first.unlockOrder ? district : first),
    undefined,
  );
  const inhabited = new Set(inhabitedDistrictCodes);
  const bounds = unlocked
    .filter((district) => district === primary || inhabited.has(district.code))
    .map(({ bounds }) => ({ ...bounds, maxX: bounds.maxX + 1, maxY: bounds.maxY + 1 }));
  if (
    placementTarget &&
    !bounds.some(
      (district) =>
        district.minX === placementTarget.minX &&
        district.maxX === placementTarget.maxX &&
        district.minY === placementTarget.minY &&
        district.maxY === placementTarget.maxY,
    )
  )
    bounds.push({ ...placementTarget });
  return bounds;
}

/** Fixed district geography gives a durable move a visibly different world position. */
export function createSettlementWorldFrame(
  districts: readonly SettlementGridBounds[],
  occupied: readonly SettlementGridBounds[],
  width: number,
  height: number,
): SettlementGridFrame | undefined {
  const outliers = occupied.filter(
    (plot) =>
      !districts.some(
        (district) =>
          plot.minX >= district.minX &&
          plot.maxX <= district.maxX &&
          plot.minY >= district.minY &&
          plot.maxY <= district.maxY,
      ),
  );
  const bounds = [...districts, ...outliers];
  const frame = fitSettlementGrid(bounds);
  if (!frame) return undefined;
  const tile = settlementTileWidth(height);
  return focusSettlementGrid(frame, bounds, width, height, tile, tile / 2);
}

/**
 * Focus a usable drag target using only the outer camera, keeping terrain and
 * buildings at the same relative scale. Use the original footprint, never the draft.
 */
export function createSettlementPlacementCamera(
  worldFrame: SettlementGridFrame,
  selectedFootprint: SettlementGridBounds,
  width: number,
  height: number,
  unobscuredViewport = false,
): SettlementCameraView {
  const tile = settlementTileWidth(height);
  const plot = projectSettlementFootprint(
    selectedFootprint,
    width,
    height,
    tile,
    tile / 2,
    worldFrame,
  );
  if (plot.width <= 0 || width <= 0 || height <= 0) return { zoom: 1, offsetX: 0, offsetY: 0 };
  const landscape = !unobscuredViewport && height < 500 && width > height;
  const freeWidth = landscape ? Math.max(84, width - 250) : width;
  const freeHeight = unobscuredViewport || landscape ? height : height * 0.56;
  const focusX = freeWidth * 0.5;
  const focusY = freeHeight * 0.76;
  // Leave the top control row clear even for the tallest square-source family.
  const maximumWidth = worldFrame.registration
    ? Math.max(44, Math.min(freeWidth * 0.7, (focusY - 56) / 1.1))
    : Math.min(freeWidth * 0.7, freeHeight * 0.85);
  const targetWidth = Math.min(maximumWidth, Math.max(144, plot.width));
  // Enough zoom to place the anchor in free space while the same terrain plate
  // still covers every viewport edge, including roomy desktop canvases.
  const cover = worldFrame.registration?.cover;
  const image = cover ?? { x: 0, y: 0, width, height };
  const coverageZoom = Math.max(
    focusX / Math.max(1, plot.anchor.x - image.x),
    (width - focusX) / Math.max(1, image.x + image.width - plot.anchor.x),
    focusY / Math.max(1, plot.anchor.y - image.y),
    (height - focusY) / Math.max(1, image.y + image.height - plot.anchor.y),
  );
  const maxZoom = SETTLEMENT_EDITOR_FIT_MAX_ZOOM;
  // Near image edges exact centering may require excessive magnification. Keep the whole
  // silhouette usable and let the covered-image clamp shift it within the free scene instead.
  const desiredZoom = Math.max(1, targetWidth / plot.width, coverageZoom);
  const zoom = worldFrame.registration
    ? Math.max(1, Math.min(maxZoom, maximumWidth / plot.width, desiredZoom))
    : Math.min(maxZoom, desiredZoom);
  return clampSettlementCamera(
    {
      zoom,
      offsetX: focusX - plot.anchor.x * zoom,
      offsetY: focusY - plot.anchor.y * zoom,
    },
    width,
    height,
    maxZoom,
    cover,
  );
}

/** Best covered view of the chosen district. Wide phone crops may require panning at min zoom. */
export function createSettlementEditorCamera(
  frame: SettlementGridFrame,
  district: SettlementGridBounds,
  width: number,
  height: number,
  unobscuredViewport = false,
): SettlementCameraView {
  const tile = settlementTileWidth(height);
  const corners = [
    [district.minX, district.minY],
    [district.maxX, district.minY],
    [district.maxX, district.maxY],
    [district.minX, district.maxY],
  ].map(([x, y]) => projectSettlementGround(x!, y!, width, height, tile, tile / 2, frame));
  const minX = Math.min(...corners.map((point) => point.x));
  const maxX = Math.max(...corners.map((point) => point.x));
  const minY = Math.min(...corners.map((point) => point.y));
  const maxY = Math.max(...corners.map((point) => point.y));
  const landscape = !unobscuredViewport && height < 500 && width > height;
  const freeWidth = landscape ? Math.max(84, width - 250) : width;
  const freeHeight = unobscuredViewport || landscape ? height : height * 0.7;
  // Upright silhouettes need breathing room above their ground cells.
  const headroom = (maxY - minY) * 0.5;
  const focusX = freeWidth * 0.5;
  const focusY = freeHeight * 0.5 + 12;
  const worldX = (minX + maxX) * 0.5;
  const worldY = (minY - headroom + maxY) * 0.5;
  const cover = frame.registration?.cover;
  // A low, off-screen island cannot be raised at zoom 1 while a portrait cover image
  // still fills the bottom edge. Increase the shared camera zoom before panning.
  const coverageZoom = cover
    ? Math.max(
        focusX / Math.max(1, worldX - cover.x),
        (width - focusX) / Math.max(1, cover.x + cover.width - worldX),
        focusY / Math.max(1, worldY - cover.y),
        (height - focusY) / Math.max(1, cover.y + cover.height - worldY),
      )
    : 1;
  const maxZoom = SETTLEMENT_EDITOR_FIT_MAX_ZOOM;
  const zoom = Math.min(
    maxZoom,
    Math.max(
      1,
      coverageZoom,
      Math.min((freeWidth - 32) / (maxX - minX), (freeHeight - 48) / (maxY - minY + headroom)),
    ),
  );
  return clampSettlementCamera(
    {
      zoom,
      offsetX: focusX - worldX * zoom,
      offsetY: focusY - worldY * zoom,
    },
    width,
    height,
    maxZoom,
    cover,
  );
}

export const SETTLEMENT_CAMERA_PROFILE = {
  id: 'lake-basin-three-quarter-v1',
  projection: 'perspective',
  yawDegrees: 45,
  pitchDegrees: 35,
  horizonRatio: 0.21,
  groundTileDepthRatio: 0.5,
  spriteGroundAnchorY: 0.91,
  cameraGridDepth: 24,
  referenceGridDepth: 10,
  groundFocalRatio: 0.28,
} as const;

export function compareSettlementGroundDepth(
  left: { gridX: number; gridY: number },
  right: { gridX: number; gridY: number },
): number {
  return left.gridX + left.gridY - (right.gridX + right.gridY) || left.gridX - right.gridX;
}

/**
 * Projects the authored settlement grid onto the illustrated lake-basin camera.
 *
 * The runtime world is deliberately 2.5D rather than a flat isometric board. It
 * uses one pinhole scale for lateral spacing, forward spacing and object size:
 * equal lateral offsets converge towards the horizon and distant structures
 * shrink by exactly the same ratio. The logical 2:1 tile already describes the
 * pitch of the ground plane, so upright art is scaled uniformly rather than
 * receiving an independent vertical squash. The server-owned grid remains
 * unchanged; this function only determines presentation.
 */
export function projectSettlementGround(
  gridX: number,
  gridY: number,
  viewportWidth: number,
  viewportHeight: number,
  tileWidth: number,
  tileHeight: number,
  frame?: SettlementGridFrame,
): SettlementGroundProjection {
  const cameraX = frame ? 4 + (gridX - frame.centerX) * frame.scale : gridX;
  const cameraY = frame ? 4 + (gridY - frame.centerY) * frame.scale : gridY;
  const gridDepth = cameraX + cameraY;
  const authoredDepth = Math.min(16, Math.max(0, gridDepth));
  const referenceDistance =
    SETTLEMENT_CAMERA_PROFILE.cameraGridDepth - SETTLEMENT_CAMERA_PROFILE.referenceGridDepth;
  const cameraDistance = SETTLEMENT_CAMERA_PROFILE.cameraGridDepth - authoredDepth;
  const perspectiveScale = referenceDistance / cameraDistance;
  const groundPitchScale =
    tileHeight / (tileWidth * SETTLEMENT_CAMERA_PROFILE.groundTileDepthRatio);

  const zoom = frame?.registration ? 1 : (frame?.view?.zoom ?? 1);
  const x = viewportWidth * 0.5 + (cameraX - cameraY) * (tileWidth / 2) * perspectiveScale;
  const y =
    viewportHeight * SETTLEMENT_CAMERA_PROFILE.horizonRatio +
    viewportHeight *
      SETTLEMENT_CAMERA_PROFILE.groundFocalRatio *
      perspectiveScale *
      groundPitchScale;
  if (frame?.registration) {
    const point = applyIslandHomography(frame.registration.forward, { x, y });
    const lateralScale = islandLateralScale(frame.registration.forward, { x, y });
    // Outside the registered projective domain there is no honest fallback coordinate.
    if (!point || !lateralScale || lateralScale < 1 / 1024 || lateralScale > 16)
      return { x: NaN, y: NaN, depthScale: NaN, groundScale: NaN };
    return {
      ...point,
      depthScale: perspectiveScale * (frame.scale ?? 1) * lateralScale,
      groundScale: perspectiveScale * (frame.scale ?? 1) * lateralScale,
    };
  }
  return {
    x: x * zoom + (frame?.view?.offsetX ?? 0),
    y: y * zoom + (frame?.view?.offsetY ?? 0),
    depthScale: perspectiveScale * (frame?.scale ?? 1) * zoom,
    groundScale: perspectiveScale * (frame?.scale ?? 1) * zoom,
  };
}

/**
 * Invert the same pinhole plane, including its grid fit and inner composition.
 * The outer pan/zoom belongs to the renderer and must be removed first. Outside
 * the authored depth range the forward projection clamps and is not invertible.
 */
export function unprojectSettlementGround(
  screenX: number,
  screenY: number,
  viewportWidth: number,
  viewportHeight: number,
  tileWidth: number,
  tileHeight: number,
  frame?: SettlementGridFrame,
): { x: number; y: number } | undefined {
  if (frame?.registration) {
    const point = applyIslandHomography(frame.registration.inverse, { x: screenX, y: screenY });
    if (!point) return undefined;
    screenX = point.x;
    screenY = point.y;
  }
  const zoom = frame?.registration ? 1 : (frame?.view?.zoom ?? 1);
  const scale = frame?.scale ?? 1;
  if (
    ![screenX, screenY, viewportWidth, viewportHeight, tileWidth, tileHeight, zoom, scale].every(
      Number.isFinite,
    ) ||
    Math.min(viewportWidth, viewportHeight, tileWidth, tileHeight, zoom, scale) <= 0
  )
    return undefined;
  const x = (screenX - (frame?.registration ? 0 : (frame?.view?.offsetX ?? 0))) / zoom;
  const y = (screenY - (frame?.registration ? 0 : (frame?.view?.offsetY ?? 0))) / zoom;
  const pitch = tileHeight / (tileWidth * SETTLEMENT_CAMERA_PROFILE.groundTileDepthRatio);
  const perspectiveScale =
    (y - viewportHeight * SETTLEMENT_CAMERA_PROFILE.horizonRatio) /
    (viewportHeight * SETTLEMENT_CAMERA_PROFILE.groundFocalRatio * pitch);
  if (perspectiveScale <= 0) return undefined;
  const depth =
    SETTLEMENT_CAMERA_PROFILE.cameraGridDepth -
    (SETTLEMENT_CAMERA_PROFILE.cameraGridDepth - SETTLEMENT_CAMERA_PROFILE.referenceGridDepth) /
      perspectiveScale;
  if (!Number.isFinite(depth) || depth < -1e-8 || depth > 16 + 1e-8) return undefined;
  const lateral = (x - viewportWidth * 0.5) / ((tileWidth / 2) * perspectiveScale);
  const cameraX = (depth + lateral) / 2;
  const cameraY = (depth - lateral) / 2;
  const point = frame
    ? { x: frame.centerX + (cameraX - 4) / scale, y: frame.centerY + (cameraY - 4) / scale }
    : { x: cameraX, y: cameraY };
  return Number.isFinite(point.x) && Number.isFinite(point.y) ? point : undefined;
}
